import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, message, Alert, Tooltip } from "antd";
import {
    FaBuilding,
    FaEnvelope,
    FaPhoneAlt,
    FaMapMarkerAlt,
    FaEye,
    FaEyeSlash,
    FaPlus,
    FaWifi,
    FaExclamationTriangle,
    FaInfo
} from "react-icons/fa";
import { IoInformationCircle } from "react-icons/io5";

const { TextArea } = Input;

const ClinicFormModal = ({
    visible,
    onCancel,
    onFinish,
    initialValues,
    loading,
    existingClinics = []
}) => {
    const [form] = Form.useForm();
    const [realTimeErrors, setRealTimeErrors] = useState({});
    const [realTimeWarnings, setRealTimeWarnings] = useState({});
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // Network status monitoring
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setRealTimeErrors({});
            setRealTimeWarnings({});
            if (initialValues) {
                form.setFieldsValue(initialValues);
            }
        }
    }, [visible, initialValues, form]);

    // Real-time validation functions
    const validateClinicName = (value) => {
        if (!value) return { error: null, warning: null };
        
        // Check for duplicate names (excluding current clinic when editing)
        const isDuplicate = existingClinics.some(clinic => 
            clinic.name.toLowerCase().trim() === value.toLowerCase().trim() && 
            (!initialValues || clinic.id !== initialValues.id)
        );
        
        let error = null;
        let warning = null;
        
        if (value.length < 2) {
            error = 'Clinic name must be at least 2 characters';
        } else if (value.length > 100) {
            error = 'Clinic name must be less than 100 characters';
        } else if (isDuplicate) {
            warning = 'A clinic with this name already exists';
        }
        
        return { error, warning };
    };
    const validateClinicNameAr = (value) => {
        if (!value) return { error: null, warning: null };
        
        // Check for duplicate names (excluding current clinic when editing)
        const isDuplicate = existingClinics.some(clinic => 
            clinic.name.toLowerCase().trim() === value.toLowerCase().trim() && 
            (!initialValues || clinic.id !== initialValues.id)
        );
        
        let error = null;
        let warning = null;
        
        if (value.length < 2) {
            error = 'Clinic name must be at least 2 characters';
        } else if (value.length > 100) {
            error = 'Clinic name must be less than 100 characters';
        } else if (isDuplicate) {
            warning = 'A clinic with this name already exists';
        }
        
        return { error, warning };
    };

    const validateEmail = (value) => {
        if (!value) return null;
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        
        if (value.length > 255) {
            return 'Email must be less than 255 characters';
        }
        
        return null;
    };

    const validatePhone = (value) => {
        if (!value) return null;
        
        // Remove all non-digit characters for validation
        const digitsOnly = value.replace(/\D/g, '');
        
        if (digitsOnly.length < 10) {
            return 'Phone number must be at least 10 digits';
        }
        
        if (digitsOnly.length > 15) {
            return 'Phone number must be less than 15 digits';
        }
        
        return null;
    };

    const validateAddress = (value) => {
        if (!value) return null;
        
        if (value.trim().length < 10) {
            return 'Address must be at least 10 characters';
        }
        
        if (value.length > 500) {
            return 'Address must be less than 500 characters';
        }
        
        return null;
    };
    const validateAddressAr = (value) => {
        if (!value) return null;
        
        if (value.trim().length < 10) {
            return 'Address must be at least 10 characters';
        }
        
        if (value.length > 500) {
            return 'Address must be less than 500 characters';
        }
        
        return null;
    };

    // Real-time validation handler
    const handleFieldChange = (fieldName, value) => {
        let error = null;
        let warning = null;
        
        switch (fieldName) {
            case 'name':
                const nameValidation = validateClinicName(value);
                error = nameValidation.error;
                warning = nameValidation.warning;
                setRealTimeWarnings(prev => ({
                    ...prev,
                    [fieldName]: warning
                }));
                break;
            case 'nameAr':
                const nameArValidation = validateClinicNameAr(value);
                error = nameArValidation.error;
                warning = nameArValidation.warning;
                setRealTimeWarnings(prev => ({
                    ...prev,
                    [fieldName]: warning
                }));
                break;
            case 'email':
                error = validateEmail(value);
                break;
            case 'phone':
                error = validatePhone(value);
                break;
            case 'detailedAddress':
                error = validateAddress(value);
                break;
            case 'detailedAddressAr':
                error = validateAddressAr(value);
                break;
            default:
                break;
        }
        
        setRealTimeErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    // Format phone number as user types
    const formatPhoneNumber = (value) => {
        if (!value) return value;
        
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;
        
        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        }
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        form.setFieldsValue({ phone: formatted });
        handleFieldChange('phone', formatted);
    };

    // Enhanced form submission with better error handling
    const handleSubmit = async (values) => {
        try {
            // Final validation before submission (only check for actual errors, not warnings)
            const nameValidation = validateClinicName(values.name);
            const nameArValidation = validateClinicNameAr(values.nameAr);
            const nameError = nameValidation.error; // Only check error, not warning
            const nameArError = nameArValidation.error; // Only check error, not warning
            
            const emailError = validateEmail(values.email);
            const phoneError = validatePhone(values.phone);
            const addressError = validateAddress(values.detailedAddress);
            
            if (nameError || nameArError|| emailError || phoneError || addressError) {
                message.error('Please fix the validation errors before submitting');
                return;
            }
            
            await onFinish(values);
        } catch (error) {
            console.error('Form submission error:', error);
            // Check if it's a network error
            if (!navigator.onLine || error.code === 'NETWORK_ERROR' || error.message?.includes('network') || error.message?.includes('fetch')) {
                message.error('Network error: Please check your internet connection and try again');
            } else {
                message.error(error.message || 'Something went wrong. Please try again.');
            }
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!visible) return;
            
            // Ctrl/Cmd + Enter to submit
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                form.submit();
            }
            
            // Escape to cancel
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible, form, onCancel]);

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <FaBuilding className="text-blue-500" />
                    <span>{initialValues ? "Edit Clinic" : "Add New Clinic"}</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button 
                    key="back" 
                    onClick={onCancel} 
                    size="large"
                    disabled={loading}
                >
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={() => form.submit()}
                    size="large"
                    className="bg-primary mt-3"
                    disabled={loading || Object.values(realTimeErrors).some(error => error !== null) || !navigator.onLine}
                    icon={loading ? null : (initialValues ? null : <FaPlus />)}
                >
                    {loading ? 
                        (initialValues ? "Updating..." : "Creating...") : 
                        (initialValues ? "Update Clinic" : "Create Clinic")
                    }
                </Button>,
            ]}
            destroyOnClose
        >
            {/* Network Status Alert */}
            {!isOnline && (
                <Alert
                    message="No Internet Connection"
                    description="Please check your internet connection. You cannot save changes while offline."
                    type="error"
                    icon={<FaWifi />}
                    showIcon
                    className="mb-4"
                />
            )}

            {/* Loading State Alert */}
            {loading && (
                <Alert
                    message={initialValues ? "Updating Clinic..." : "Creating Clinic..."}
                    description="Please wait while we process your request."
                    type="info"
                    showIcon
                    className="mb-4"
                />
            )}
            
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
                size="large"
            >
                <Form.Item
                    name="name"
                    label={
                        <span className="flex items-center gap-2">
                            Clinic Name
                            <Tooltip title="Enter a unique name for your clinic (2-100 characters)">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input clinic name!' },
                        { validator: (_, value) => {
                            const nameValidation = validateClinicName(value);
                            const error = nameValidation.error; // Only reject on actual errors, not warnings
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.name ? 'error' : (realTimeWarnings.name ? 'warning' : '')}
                    help={realTimeErrors.name || (realTimeWarnings.name && (
                        <span style={{ color: '#faad14' ,fontSize:"12px"}}>
                            {realTimeWarnings.name}
                        </span>
                    ))}
                >
                    <Input 
                        prefix={<FaBuilding className="text-gray-400" />} 
                        placeholder="Enter clinic name (e.g., City Medical Center)"
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        showCount
                        maxLength={100}
                    />
                </Form.Item>
                <Form.Item
                    name="nameAr"
                    label={
                        <span className="flex items-center gap-2">
                            Clinic Name (In arabic)
                            <Tooltip title="Enter a unique name for your clinic (2-100 characters)">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input clinic name!' },
                        { validator: (_, value) => {
                            const nameArValidation = validateClinicNameAr(value);
                            const error = nameArValidation.error; // Only reject on actual errors, not warnings
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.nameAr ? 'error' : (realTimeWarnings.nameAr ? 'warning' : '')}
                    help={realTimeErrors.nameAr || (realTimeWarnings.nameAr && (
                        <span style={{ color: '#faad14' ,fontSize:"12px"}}>
                            {realTimeWarnings.name}
                        </span>
                    ))}
                >
                    <Input 
                        prefix={<FaBuilding className="text-gray-400" />} 
                        placeholder="Enter clinic name (e.g., City Medical Center)"
                        onChange={(e) => handleFieldChange('nameAr', e.target.value)}
                        showCount
                        maxLength={100}
                    />
                </Form.Item>

                <Form.Item
                    name="email"
                    label={
                        <span className="flex items-center gap-2">
                            Email Address
                            <Tooltip title="Enter a valid email address for clinic communications">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input email!' },
                        { validator: (_, value) => {
                            const error = validateEmail(value);
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.email ? 'error' : ''}
                    help={realTimeErrors.email}
                >
                    <Input 
                        prefix={<FaEnvelope className="text-gray-400" />} 
                        placeholder="Enter email address (e.g., info@clinic.com)"
                        type="email"
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        showCount
                        maxLength={255}
                    />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label={
                        <span className="flex items-center gap-2">
                            Phone Number
                            <Tooltip title="Enter phone number (10-15 digits). It will be auto-formatted as you type">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input phone number!' },
                        { validator: (_, value) => {
                            const error = validatePhone(value);
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.phone ? 'error' : ''}
                    help={realTimeErrors.phone}
                >
                    <Input 
                        prefix={<FaPhoneAlt className="text-gray-400" />} 
                        placeholder="123-456-7890"
                        onChange={handlePhoneChange}
                    />
                </Form.Item>

                <Form.Item
                    name="detailedAddress"
                    label={
                        <span className="flex items-center gap-2">
                            Full Address
                            <Tooltip title="Enter complete address including street, city, and postal code (minimum 10 characters)">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input address!' },
                        { validator: (_, value) => {
                            const error = validateAddress(value);
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.address ? 'error' : ''}
                    help={realTimeErrors.address}
                >
                    <TextArea 
                        prefix={<FaMapMarkerAlt className="text-gray-400" />} 
                        placeholder="Enter full address including street, city, and postal code"
                        rows={2}
                        onChange={(e) => handleFieldChange('detailedAddress', e.target.value)}
                        showCount
                        maxLength={500}
                    />
                </Form.Item>
                <Form.Item
                    name="detailedAddressAr"
                    label={
                        <span className="flex items-center gap-2">
                            Full Address
                            <Tooltip title="Enter complete address including street, city, and postal code (minimum 10 characters)">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle/> </span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input address!' },
                        { validator: (_, value) => {
                            const error = validateAddressAr(value);
                            return error ? Promise.reject(error) : Promise.resolve();
                        }}
                    ]}
                    validateStatus={realTimeErrors.detailedAddressAr ? 'error' : ''}
                    help={realTimeErrors.detailedAddressAr}
                >
                    <TextArea 
                        prefix={<FaMapMarkerAlt className="text-gray-400" />} 
                        placeholder="Enter full address including street, city, and postal code"
                        rows={2}
                        onChange={(e) => handleFieldChange('address', e.target.value)}
                        showCount
                        maxLength={500}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ClinicFormModal;